import { createSelector } from '@reduxjs/toolkit';

import { EnvironmentProduction, EnvironmentSandbox } from 'dashboard/models/environment';
import { InquiryTemplate, InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { Template, TemplateFilterOption } from 'dashboard/models/template';
import {
  VerificationTemplate,
  VerificationTemplateFilterOption,
} from 'dashboard/models/verification-template';
import { RootState } from 'dashboard/redux/store';
import { ApiModel } from 'lib/interfaces/api';
import { isCreateEnabledTemplate } from 'lib/utils/template';

const getTemplates = (state: RootState): Template[] => state.organization.templates;
const getTemplateFilterOptions = (state: RootState): TemplateFilterOption[] =>
  state.organization.templateFilterOptions;
const getInquiryTemplates = (state: RootState): InquiryTemplate[] =>
  state.organization.inquiryTemplates;
const getInquiryTemplateFilterOptions = (state: RootState): InquiryTemplateFilterOption[] =>
  state.organization.inquiryTemplateFilterOptions;
const getVerificationTemplates = (state: RootState): VerificationTemplate[] =>
  state.organization.verificationTemplates;
const getVerificationTemplateFilterOptions = (
  state: RootState,
): VerificationTemplateFilterOption[] => state.organization.verificationTemplateFilterOptions;

export const getInquiryTags = (state: RootState): Tag[] => state.tags.tags[ApiModel.Inquiry];

export const getReportTags = (state: RootState): Tag[] => state.tags.tags[ApiModel.Report];

const getReportTemplates = (state: RootState): ReportTemplate[] =>
  state.reportTemplates.reportTemplates;

export const getSortedReportTemplates = createSelector(
  [getReportTemplates],
  (templates): ReportTemplate[] =>
    [...templates].sort((a, b) => a.attributes.name.localeCompare(b.attributes.name)),
);

// This can be removed after all templates are migrated to be inquiry templates.
export const getAllTemplates = createSelector(
  [getTemplates, getInquiryTemplates],
  (templates, inquiryTemplates): Array<Template | InquiryTemplate> =>
    [...templates, ...inquiryTemplates].sort((a, b) =>
      a.attributes.name.localeCompare(b.attributes.name),
    ),
);

export const getAllTemplateFilterOptions = createSelector(
  [getTemplateFilterOptions, getInquiryTemplateFilterOptions],
  (
    templateFilterOptions,
    inquiryTemplateFilterOptions,
  ): Array<TemplateFilterOption | InquiryTemplateFilterOption> =>
    [...templateFilterOptions, ...inquiryTemplateFilterOptions].sort((a, b) =>
      a.name.localeCompare(b.name),
    ),
);

export const getAllVerificationTemplates = createSelector(
  [getVerificationTemplates],
  (verificationTemplates): Array<VerificationTemplate> =>
    verificationTemplates.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name)),
);

export const getAllVerificationTemplateFilterOptions = createSelector(
  [getVerificationTemplateFilterOptions],
  (verificationTemplateFilterOptions): Array<VerificationTemplateFilterOption> =>
    verificationTemplateFilterOptions.sort((a, b) => a.name.localeCompare(b.name)),
);

export const getAllEnabledTemplates = createSelector(
  [getTemplates, getInquiryTemplates],
  (templates, inquiryTemplates): Array<Template | InquiryTemplate> =>
    [...templates, ...inquiryTemplates]
      .filter(isCreateEnabledTemplate)
      .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name)),
);

export const getSandboxEnvironments = createSelector(
  [(state: RootState) => state.organization.environments],
  (environments) =>
    environments.filter(
      (environment): environment is EnvironmentSandbox =>
        environment.type === ApiModel.EnvironmentSandbox,
    ),
);

export const getProductionEnvironments = createSelector(
  [(state: RootState) => state.organization.environments],
  (environments) =>
    environments.filter(
      (environment): environment is EnvironmentProduction =>
        environment.type === ApiModel.EnvironmentProduction,
    ),
);
